/**
 * Card #303: a panel actor's pose is a named pose (`PANEL_POSES`) or a frame of a Humanoid clip, `clip:<clipId>@<t>` (t = normalized
 * time 0..1) with an optional `:mirror`. A port of shared-cs `Content/PanelPoseRefs.cs`; the clip ids are the generated `PANEL_CLIPS`.
 */
import type { PanelRulesDef } from './types';
import { PANEL_CLIPS, type PanelClip } from './panelClips.g';

export const CLIP_PREFIX = 'clip:';
export const MIRROR_SUFFIX = ':mirror';
/** The most alternates a named pose may list (`panelRules.poses[].variants`). */
export const MAX_POSE_VARIANTS = 8;

export type ClipFrame = { clip: string; time: number; mirror: boolean };

export const isClipPose = (pose: string | null | undefined): pose is string =>
  pose != null && pose.startsWith(CLIP_PREFIX);

/** `PanelPoseRefs.TryParse`: syntax only (letters, digits and `_` for the id; a plain decimal 0..1 for t). */
export function parseClipPose(pose: string | null | undefined): ClipFrame | null {
  if (!isClipPose(pose)) return null;
  let body = pose.slice(CLIP_PREFIX.length);
  let mirror = false;
  if (body.endsWith(MIRROR_SUFFIX)) {
    mirror = true;
    body = body.slice(0, -MIRROR_SUFFIX.length);
  }
  const at = body.indexOf('@');
  if (at <= 0 || at !== body.lastIndexOf('@') || at === body.length - 1) return null;
  const clip = body.slice(0, at);
  const t = body.slice(at + 1);
  if (!/^[A-Za-z0-9_]+$/.test(clip) || !/^[0-9.]+$/.test(t)) return null;
  const time = Number(t);
  if (!Number.isFinite(time) || time < 0 || time > 1) return null;
  return { clip, time, mirror };
}

/** `PanelPoseRefs.Format`: t rounded to 3 decimals, no trailing zeros. */
export function formatClipPose(clip: string, time: number, mirror: boolean): string {
  const t = Number.isNaN(time) || time < 0 ? 0 : time > 1 ? 1 : time;
  return `${CLIP_PREFIX}${clip}@${String(Math.round(t * 1000) / 1000)}${mirror ? MIRROR_SUFFIX : ''}`;
}

const byId = new Map<string, PanelClip>(PANEL_CLIPS.map((c) => [c.id, c]));
export const findClip = (id: string | null | undefined): PanelClip | undefined =>
  id ? byId.get(id) : undefined;

/** `PanelPoseRefs.Check`: why a clip pose can't be used (the server's words), or null. */
export function checkClipPose(pose: string, zombie: boolean): string | null {
  const f = parseClipPose(pose);
  if (!f) return `has a malformed clip pose '${pose}' (clip:<clipId>@<0..1>, optional :mirror)`;
  const info = findClip(f.clip);
  if (!info) return `has pose '${pose}' with unknown clip '${f.clip}'`;
  if (zombie && info.set !== 'zombie')
    return `is a zombie and can't take pose clip '${f.clip}' (a zombie samples Z_ clips only)`;
  return null;
}

/** `PanelPoseRefs.VariantCount`: how many variants an actor can pick for a named pose (at least 1). */
export function poseVariantCount(
  rules: PanelRulesDef | null | undefined,
  pose: string,
  zombie: boolean,
): number {
  const row = (rules?.poses ?? []).find((r) => r?.pose === pose);
  const list = zombie ? row?.zombieVariants : row?.variants;
  return list && list.length > 0 ? list.length : 1;
}
